import React from "react";
import "./Policies.scss";

const Policies = () => {
  return (
    <div className="privacy-policy-container">
      <h1>Privacy Policy</h1>
      <p>
        At NeoSapien, your privacy is our priority. This Privacy Policy explains
        how we collect, use, protect, and share your data when using the Neo 1
        wearable and its associated app, including services like transcription,
        emotion analysis, feedback, and AI chat services.
      </p>
      <h2>1. Our Commitment to Privacy</h2>
      <p>
        We are committed to ensuring your personal information remains private
        and secure. From the design of our products to the integration of AI
        services, we prioritize privacy and transparency in how we handle your
        data.
      </p>
      <h2>2. How We Ensure Your Privacy</h2>
      <p>To maintain your trust, we:</p>
      <ul>
        <li>
          • Use end-to-end encryption technologies to secure your data. Your
          data is encrypted at rest and in-transit.
        </li>
        <li>• Regularly audit and test our systems for vulnerabilities.</li>
        <li>
          • Minimize data collection to only what is necessary for
          functionality.
        </li>
        <li>
          • Partner only with trusted third-party providers to process data
          securely.
        </li>
        <li>• Comply with data protection laws and regulations.</li>
      </ul>
      <h2>3. What Data We Collect and Why</h2>
      <h3>A. Personal Information</h3>
      <ul>
        <li>
          • Account Details: Name, email, and profile information to create and
          manage your account.
        </li>
        <li>
          • Payment Information: Details required for secure transactions,
          processed via third-party providers.
        </li>
      </ul>
      <h3>B. Device and App Usage Data</h3>
      <ul>
        <li>
          • Transcription Data: Audio recordings, transcriptions, and summaries
          to deliver accurate insights and emotion analysis.
        </li>
        <li>
          • Processed Data: Information derived from conversations, such as
          feedback on meeting performance or emotional tone.
        </li>
        <li>
          • AI Chat Data: Inputs from users engaging with AI chat services for
          conversation, queries, or assistance.
        </li>
      </ul>
      <h3>C. Location Data</h3>
      <p>
        With your explicit consent, we collect your location to enable the app
        to run in the background and to provide location services.
      </p>
      <h3>D. Technical Data</h3>
      <p>
        IP address, browser type, and operating system details to improve
        performance and troubleshoot issues.
      </p>
      <h2>4. App Permissions Policy for Neo 1</h2>
      <p>
        Neo 1 requires certain permissions to function optimally while ensuring
        user privacy. Microphone access is essential for voice fingerprinting,
        allowing the app to recognize only the user{"'"}s speech and provide
        personalized insights—other participants{"'"} voices are not identified
        or stored. For iOS users, notifications must be enabled to receive
        alerts on listening modes, memory generation, and other important
        updates. For Android users, notifications, location services, and
        disabling battery optimization are required to ensure continuous
        operation without system restrictions. These permissions allow Neo 1 to
        function seamlessly while maintaining strict privacy controls. Users can
        manage permissions anytime in device settings.
        <br /> <br />
        For more details, contact support@neosapien.xyz
      </p>
      <h2>5. How We Use Your Data</h2>
      <p>We use your data to:</p>
      <ul>
        <li>
          • Deliver personalized services, including transcription, emotion
          analysis, and feedback.
        </li>
        <li>
          • Provide real-time insights and accurate transcription services.
        </li>
        <li>
          • Enable AI chat features, where users can engage with conversational
          AI for assistance.
        </li>
        <li>• Enhance app features and ensure seamless functionality.</li>
        <li>• Conduct research and development to improve our services.</li>
        <li>• Communicate updates, alerts, and customer support.</li>
        <li>
          • Comply with legal obligations and enforce our Terms of Service.
        </li>
      </ul>
      <h2>6. Use of Third-Party Services</h2>
      <p>
        To deliver certain features, we use trusted third-party services of
        cloud infrastructure providers, leading foundational LLM models,
        speech-to-text service providers, and others to process your data. These
        services assist with:
      </p>
      <ul>
        <li>• AI-driven chat capabilities</li>
        <li>• Advanced transcription and summarization</li>
        <li>• Emotion and sentiment analysis</li>
      </ul>
      <h2>7. How We Process Your Data</h2>
      <p>Your data is processed securely using the following measures:</p>
      <ul>
        <li>
          • Data Encryption: All data is encrypted in transit and at rest.
        </li>
        <li>
          • Access Control: Restricted access to authorized personnel only.
        </li>
        <li>
          • Secure Servers: Cloud-based storage with industry-standard security
          protocols.
        </li>
        <li>
          • Data Retention: Data such as chat inputs and transcriptions are
          stored for providing services unless deleted by you.
        </li>
      </ul>
      <h2>8. Policies on Data Transfer and Sharing</h2>
      <p>
        We prioritize the confidentiality and security of your data. Data
        transfer and sharing are conducted solely to provide the services you
        expect and enhance your experience.
      </p>
      <h3>Data Transfer Policies</h3>
      <h4>International Data Transfers</h4>
      <ul>
        <li>
          • Your data may be transferred to, processed, and stored on servers
          located in countries outside your residence.
        </li>
        <li>
          • We ensure such transfers comply with applicable data protection
          laws.
        </li>
        <li>
          • Data transfers are conducted using industry-standard safeguards.
        </li>
      </ul>
      <h3>Data Sharing Policies</h3>
      <p>We share your data with:</p>
      <ul>
        <li>• Payment Providers: To process orders and payments securely</li>
        <li>
          • Logistics Providers: To deliver your Neo 1 device and related
          accessories
        </li>
        <li>
          • AI Partners: For features like transcription, chat services, and
          emotion analysis
        </li>
        <li>• Cloud Storage Providers: For secure storage of your data</li>
      </ul>
      <h2>9. Your Privacy Choices</h2>
      <p>You have full control over your data:</p>
      <ul>
        <li>• Conversation Deletion: Delete any conversation via the app</li>
        <li>
          • Data Deletion: Request deletion of your account and associated data
        </li>
      </ul>
      <h2>10. How We Protect Your Information</h2>
      <ul>
        <li>• End-to-end encryption for data protection</li>
        <li>• Regular system audits and updates</li>
        <li>• Multi-layered security protocols</li>
      </ul>
      <h2>11. Children&#39;s Privacy</h2>{" "}
      <p>
        Neo 1 is not intended for children under the age of 13. We do not
        knowingly collect their data. If we discover such data has been
        collected, we will delete it promptly.
      </p>
      <h2>12. Third-Party Links</h2>
      <p>
        Our app or website may include links to third-party services. We are not
        responsible for their privacy practices and encourage you to review
        their policies.
      </p>
      <h2>13. Changes to This Privacy Policy</h2>
      <p>
        We may update this policy periodically. Any significant changes will be
        communicated through email or app notifications. The latest version will
        always be available on our website.
      </p>
      <h2>14. Contact Us</h2>
      <p>
        If you have any questions or concerns about this Privacy Policy, please
        contact us:
        <br />
        Email: support@neosapien.xyz
      </p>
    </div>
  );
};

export default Policies;
